import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { CommandDialog, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '@/components/ui/command';
import { useUserRole } from '@/hooks/useUserRole';
import { useTenant } from '@/hooks/useTenant';
import { useLocalization } from '@/hooks/useLocalization';
import { visibleSections, VIEW_LABELS } from '@/lib/navigation';

interface CommandPaletteProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onNavigate: (view: string) => void;
}

/** Cmd/Ctrl+K: jump to any view the current role can open (plus Integrations). */
const CommandPalette = ({ open, onOpenChange, onNavigate }: CommandPaletteProps) => {
  const { role } = useUserRole();
  const { tenantSlug } = useTenant();
  const navigate = useNavigate();
  const { t } = useLocalization();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key.toLowerCase() === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        onOpenChange(!open);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onOpenChange]);

  const run = (fn: () => void) => { onOpenChange(false); fn(); };
  const path = (suffix: string) => (tenantSlug ? `/${tenantSlug}${suffix}` : suffix);

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange}>
      <CommandInput placeholder={t('Go to a page…')} />
      <CommandList>
        <CommandEmpty>{t('No matching page.')}</CommandEmpty>
        {visibleSections(role).map((s) => (
          <CommandGroup key={s.key} heading={t(s.label)}>
            {s.views.map((v) => (
              <CommandItem key={v} value={`${s.label} ${VIEW_LABELS[v] ?? v} ${t(VIEW_LABELS[v] ?? v)}`} onSelect={() => run(() => onNavigate(v))}>
                {t(VIEW_LABELS[v] ?? v)}
              </CommandItem>
            ))}
          </CommandGroup>
        ))}
        <CommandGroup heading={t('Workspace')}>
          <CommandItem value="Integrations connect tools" onSelect={() => run(() => navigate(path('/integrations')))}>{t('Integrations')}</CommandItem>
        </CommandGroup>
      </CommandList>
    </CommandDialog>
  );
};

export default CommandPalette;
